import { Text, Box, Stack, Grid } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import React from 'react';

// Constants for path patterns
const CAMPAIGN_PATH_PATTERNS = ['campaign', '.campaign'];
const FSM_UNDER_CAMPAIGN_PATTERNS = ['campaign.fsm', '.campaign.fsm'];

// Helper function to check if a string is valid JSON
export const isJSONString = (value: string): boolean => {
  try {
    JSON.parse(value);
    return value.trim().startsWith('{') || value.trim().startsWith('[');
  } catch {
    return false;
  }
};

// Helper function to convert snake_case or camelCase to user-friendly format
export const formatAttributeName = (name: string): string => {
  // Handle nested paths (e.g., "user_flags.aggressive_positivity_available")
  if (name.includes('.')) {
    const parts = name.split('.');
    // For Campaign object type, skip the "campaign" prefix and only show the child name
    if (parts.length > 1 && parts[0].toLowerCase() === 'campaign') {
      const remaining = parts.slice(1);
      // Also skip "fsm" level for Campaign
      if (remaining.length > 1 && remaining[0].toLowerCase() === 'fsm') {
        return formatAttributeName(remaining.slice(1).join('.'));
      }
      return formatAttributeName(remaining.join('.'));
    }
    return parts.map((part) => formatAttributeName(part)).join(': ');
  }
  // Replace underscores and hyphens with spaces
  let formatted = name.replace(/[_-]/g, ' ');
  // Split by camelCase boundaries (lowercase followed by uppercase)
  formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Capitalize first letter of each word, but preserve "AI" as uppercase
  return formatted
    .split(' ')
    .map((word) => {
      const lowerWord = word.toLowerCase();
      // Preserve "AI" as uppercase
      if (lowerWord === 'ai') {
        return 'AI';
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Helper function to format boolean values
export const formatBooleanValue = (value: boolean): string => {
  return value ? 'yes' : 'no';
};

// Helper function to parse and format duration strings (e.g., "168h0m0s" -> "1 week")
export const formatDuration = (value: string): string => {
  // Match patterns like "168h0m0s", "24h30m15s", "30m", "5s", "1w2d3h4m5s", etc.
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  let days = 0;
  let weeks = 0;

  // Match weeks (e.g., "1w")
  const weekMatch = value.match(/(\d+)w/);
  if (weekMatch != null) weeks = parseInt(weekMatch[1], 10);

  // Match days (e.g., "2d")
  const dayMatch = value.match(/(\d+)d/);
  if (dayMatch != null) days = parseInt(dayMatch[1], 10);

  // Match hours (e.g., "168h")
  const hourMatch = value.match(/(\d+)h/);
  if (hourMatch != null) hours = parseInt(hourMatch[1], 10);

  // Match minutes (e.g., "30m")
  const minuteMatch = value.match(/(\d+)m/);
  if (minuteMatch != null) minutes = parseInt(minuteMatch[1], 10);

  // Match seconds (e.g., "15s")
  const secondMatch = value.match(/(\d+)s/);
  if (secondMatch != null) seconds = parseInt(secondMatch[1], 10);

  // If no matches found, return as-is
  if (weekMatch == null && dayMatch == null && hourMatch == null && minuteMatch == null && secondMatch == null) {
    return value;
  }
  // Convert everything to seconds for calculation
  const totalSeconds = weeks * 7 * 24 * 60 * 60 + days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;

  if (totalSeconds === 0) return '0 seconds';

  // Convert to appropriate units
  const weeksCount = Math.floor(totalSeconds / (7 * 24 * 60 * 60));
  const daysCount = Math.floor((totalSeconds % (7 * 24 * 60 * 60)) / (24 * 60 * 60));
  const hoursCount = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutesCount = Math.floor((totalSeconds % (60 * 60)) / 60);
  const secondsCount = totalSeconds % 60;

  const parts: string[] = [];
  if (weeksCount > 0) parts.push(`${weeksCount} ${weeksCount === 1 ? 'week' : 'weeks'}`);
  if (daysCount > 0) parts.push(`${daysCount} ${daysCount === 1 ? 'day' : 'days'}`);
  if (hoursCount > 0) parts.push(`${hoursCount} ${hoursCount === 1 ? 'hour' : 'hours'}`);
  if (minutesCount > 0) parts.push(`${minutesCount} ${minutesCount === 1 ? 'minute' : 'minutes'}`);
  if (secondsCount > 0) parts.push(`${secondsCount} ${secondsCount === 1 ? 'second' : 'seconds'}`);

  const result = parts.join(', ');
  return result !== '' ? result : '0 seconds';
};

// Helper function to check if a string is a duration format
export const isDurationString = (value: string): boolean => {
  // Check for patterns like "168h0m0s", "24h30m15s", "30m", "5s", "1w2d3h4m5s", etc.
  return /^(\d+[wdhms])+$/.test(value);
};

// Helper function to format values, mapping booleans to yes/no and formatting durations
export const formatValue = (value: unknown): string => {
  if (value == null) {
    return 'none';
  }
  if (typeof value === 'boolean') {
    return formatBooleanValue(value);
  }
  // Don't convert numeric 0/1 to yes/no - only convert actual boolean values
  if (typeof value === 'string') {
    // Check if it's a duration string and format it
    if (isDurationString(value)) {
      return formatDuration(value);
    }
  }
  return String(value);
};

// Helper function to check if a string is an ISO date string
export const isISODateString = (value: string): boolean => {
  // Match ISO 8601 date strings like "2025-11-05T23:46:25Z" or "2025-11-05T23:46:25.123Z"
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  return isoDateRegex.test(value);
};

// Helper function to format ISO date string to human-friendly format
export const formatHumanFriendlyDateFromISO = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Check if it's today
    const isToday = dateOnly.getTime() === today.getTime();

    // Check if it's yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = dateOnly.getTime() === yesterday.getTime();

    // Format time with seconds (e.g., "9:40:32 pm")
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 !== 0 ? hours % 12 : 12;
    const timeStr = `${displayHours}:${minutes}:${seconds} ${ampm}`;

    // Get timezone abbreviation
    const timezoneName =
      date
        .toLocaleString('en-US', {
          timeZoneName: 'short',
        })
        .split(' ')
        .pop() ?? '';

    // Format date prefix
    let datePrefix = '';
    if (isToday) {
      datePrefix = 'Today at';
    } else if (isYesterday) {
      datePrefix = 'Yesterday at';
    } else {
      // Format as "Jan 15 at" for other dates
      datePrefix =
        date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
        }) + ' at';
    }

    return `${datePrefix} ${timeStr} (${timezoneName ?? ''})`;
  } catch {
    return '';
  }
};

// Helper function to format date to human-friendly format (for Date objects)
export const formatHumanFriendlyDate = (date: Date, timezone: string | undefined): string => {
  try {
    if (isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Check if it's today
    const isToday = dateOnly.getTime() === today.getTime();

    // Check if it's yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = dateOnly.getTime() === yesterday.getTime();

    // Format time with seconds (e.g., "9:40:32 pm")
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 !== 0 ? hours % 12 : 12;
    const timeStr = `${displayHours}:${minutes}:${seconds} ${ampm}`;

    // Get timezone abbreviation
    const timezoneName =
      timezone ??
      date
        .toLocaleString('en-US', {
          timeZoneName: 'short',
        })
        .split(' ')
        .pop() ??
      '';

    // Format date prefix
    let datePrefix = '';
    if (isToday) {
      datePrefix = 'Today at';
    } else if (isYesterday) {
      datePrefix = 'Yesterday at';
    } else {
      // Format as "Jan 15 at" for other dates
      datePrefix =
        date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
        }) + ' at';
    }

    return `${datePrefix} ${timeStr} (${timezoneName ?? ''})`;
  } catch {
    return '';
  }
};

// Helper function to check if a path is a campaign object
export const isCampaignPath = (path: string): boolean => {
  return CAMPAIGN_PATH_PATTERNS.some((pattern) => {
    if (pattern.startsWith('.')) {
      // Pattern like '.campaign' - safe to use endsWith as it requires dot separator
      return path.endsWith(pattern);
    }
    // Pattern like 'campaign' - check exact match or ends with '.campaign' (requires dot separator)
    return path === pattern || path.endsWith(`.${pattern}`);
  });
};

// Helper function to check if a path is fsm under campaign
export const isFsmUnderCampaignPath = (path: string): boolean => {
  return FSM_UNDER_CAMPAIGN_PATTERNS.some((pattern) => {
    if (pattern.startsWith('.')) {
      // Pattern like '.campaign.fsm' - safe to use endsWith as it requires dot separator
      return path.endsWith(pattern);
    }
    // Pattern like 'campaign.fsm' - check exact match or ends with '.campaign.fsm' (requires dot separator)
    return path === pattern || path.endsWith(`.${pattern}`);
  });
};

// Helper component to render JSON in a user-friendly table/list format
export const FriendlyJSONDisplay: React.FC<{ value: unknown; textColor?: string; parentKey?: string }> = ({
  value,
  textColor,
  parentKey,
}) => {
  // Handle null/undefined
  if (value == null) {
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        none
      </Text>
    );
  }

  // If it's a string, check if it's JSON and parse it
  if (typeof value === 'string') {
    if (isJSONString(value)) {
      try {
        const parsed = JSON.parse(value);
        return <FriendlyJSONDisplay value={parsed} textColor={textColor} parentKey={parentKey} />;
      } catch {
        // If parsing fails, fall through to display as string
      }
    }
    // Check if it's an ISO date string
    if (isISODateString(value)) {
      return (
        <Text
          size="s"
          style={{
            fontFamily: 'monospace',
            color: textColor,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {formatHumanFriendlyDateFromISO(value)}
        </Text>
      );
    }
    // Display as regular string
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {value}
      </Text>
    );
  }

  if (Array.isArray(value)) {
    return (
      <Stack gap="100">
        {value.map((item, index) => (
          <Box
            key={index}
            p="150"
            style={{
              borderRadius: themeVars.radius['4'],
              border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
              backgroundColor: themeVars.color['surface-base'],
            }}
          >
            <Text size="xs" weight="medium" color="subtle" style={{ marginBottom: '8px' }}>
              Item {index + 1}:
            </Text>
            <FriendlyJSONDisplay value={item} textColor={textColor} parentKey={parentKey} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const entries = Object.entries(obj);

    // Check if all values are simple primitives (string, number, boolean, null)
    const allSimple = entries.every(([, val]) => {
      const type = typeof val;
      return type === 'string' || type === 'number' || type === 'boolean' || val == null;
    });

    // Check if this is user_flags or account_flags - render on separate full-width rows
    const parentKeyLower = parentKey?.toLowerCase() ?? '';
    const isFlagsObject =
      parentKeyLower === 'user_flags' ||
      parentKeyLower === 'userflags' ||
      parentKeyLower === 'account_flags' ||
      parentKeyLower === 'accountflags';

    if (isFlagsObject) {
      // Render flags in two columns to save vertical space
      return (
        <Grid columns={2} gap="100">
          {entries.map(([key, val]) => (
            <Box
              key={key}
              display="flex"
              gap="150"
              px="300"
              py="200"
              style={{
                width: '100%',
                minWidth: 0,
              }}
            >
              <Text
                size="s"
                weight="medium"
                style={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flexShrink: 0,
                  whiteSpace: 'normal',
                  minWidth: '150px',
                }}
              >
                {formatAttributeName(key)}:
              </Text>
              <Text
                size="s"
                style={{
                  fontFamily: 'monospace',
                  color: textColor,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatValue(val)}
              </Text>
            </Box>
          ))}
        </Grid>
      );
    }

    // Use grid layout for objects with many simple key-value pairs
    if (allSimple && entries.length > 3) {
      return (
        <Grid columns="repeat(auto-fill, minmax(250px, 1fr))" gap="100">
          {entries.map(([key, val]) => (
            <Box
              key={key}
              display="flex"
              gap="100"
              px="200"
              py="100"
              style={{
                minWidth: 0,
                width: '100%',
              }}
            >
              <Text
                size="s"
                weight="medium"
                style={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flexShrink: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatAttributeName(key)}:
              </Text>
              <Text
                size="s"
                style={{
                  fontFamily: 'monospace',
                  color: textColor,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatValue(val)}
              </Text>
            </Box>
          ))}
        </Grid>
      );
    }

    // Use vertical list for complex objects or small objects
    return (
      <Stack gap="050">
        {entries.map(([key, val]) => (
          <Box
            key={key}
            display="flex"
            gap="150"
            py="100"
            style={{
              borderBottom: `${themeVars.border['width-0125']} solid ${themeVars.color['border-subtle']}`,
              minWidth: 0, // Allow flex item to shrink below its content size
            }}
          >
            <Text
              size="s"
              weight="medium"
              style={{
                minWidth: '150px',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                flexShrink: 0,
              }}
            >
              {formatAttributeName(key)}:
            </Text>
            <Box style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              <FriendlyJSONDisplay value={val} textColor={textColor} parentKey={key} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  // For other primitive values (numbers, booleans)
  if (typeof value === 'boolean') {
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {formatBooleanValue(value)}
      </Text>
    );
  }
  return (
    <Text
      size="s"
      style={{
        fontFamily: 'monospace',
        color: textColor,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {String(value)}
    </Text>
  );
};
